import { Injectable, inject } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { roundAmericanOdds } from '@frontend/sports/odds/feature/converters';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { Decimal } from 'decimal.js';

import { Fraction } from './fraction';
import { CalculatedOdds, Odds } from './odds';
import { OddsConverter } from './odds-converter';
import { OddsOperations } from './operations';

const DECIMAL_PLACES = 2;
const SUMMARY_VALUE_THRESHOLD = 1.01;
type PartialOdds = Partial<Odds> | Partial<CalculatedOdds>;

export interface OddsFormatInput {
    displayFormat: OddsDisplayFormat;
    cutZero?: boolean;
    isSummary?: boolean;
    fromFormat?: OddsDisplayFormat;
    maxDecimalPlaces?: number;
    useOddsMultiplicationFormat?: boolean;
    roundPriceValues?: boolean;
}

export const defaultOddsFormatInput = Object.freeze({
    cutZero: false,
    isSummary: false,
    maxDecimalPlaces: 3,
    useOddsMultiplicationFormat: false,
});

/**
 * Returns a default string representation for invalid odds based on the display format.
 * @param  displayFormat - The format type for displaying odds.
 * @returns A string representing invalid odds for the specified format.
 */
export const formatInvalidOdds = (displayFormat: OddsDisplayFormat): string => {
    switch (displayFormat) {
        case OddsDisplayFormat.US:
            return '---';
        case OddsDisplayFormat.UK:
            return '-/-';
        default:
            return '-.--';
    }
};

/**
 * Checks if the given odds object is valid for a specific display format.
 * @param  format - The odds display format (EU, UK, or US).
 * @param  odds - The odds object containing potential decimal, fractional, or moneyline values.
 * @returns  - True if the odds object is valid for the specified format, false otherwise.
 */
//TODO below type guard should be improved, we need to have a type for valid format odds (something like ValidOdds<Format> to replace Odds | CalculatedOdds), because technically below function just ensures that the type is valid, which is true since we only care about selected format availability
export const isFormatValid = (format: OddsDisplayFormat, odds: PartialOdds): odds is Odds | CalculatedOdds => {
    switch (format) {
        case OddsDisplayFormat.EU:
            return odds.decimals ? OddsOperations.isDecimalOddsValid(odds.decimals) : false;
        case OddsDisplayFormat.UK:
            return odds.fractional ? OddsOperations.isFractionalOddsValid(odds.fractional) : false;
        case OddsDisplayFormat.US:
            return odds.moneyline ? OddsOperations.isMoneyLineOddsValid(odds.moneyline) : false;
    }
};

/**
 * Converts a fractional odds representation to the UK odds format with denominator of 1 (e.g., "3/1").
 * @param  fractional - The fractional odds data.
 * @param  maxDecimalPlaces - Maximum number of decimal places to use in formatting.
 * @param  isSummary - Indicates if the summary format should be used.
 * @returns - The UK-formatted fractional odds with denominator of 1 as a string.
 */
export const toSimplifiedUKOdds = (
    fractional: { numerator: number; denominator: number } | Fraction,
    maxDecimalPlaces: number,
    isSummary: boolean,
) => {
    const priceValue = new Decimal(fractional.numerator).dividedBy(new Decimal(fractional.denominator));
    const decimalOdds = OddsConverter.fractionToDecimal(Fraction.create(fractional));

    const numerator = priceValue.toFixed(
        priceValue.decimalPlaces() > DECIMAL_PLACES && (!isSummary || decimalOdds.lessThan(SUMMARY_VALUE_THRESHOLD))
            ? maxDecimalPlaces
            : Math.min(priceValue.decimalPlaces(), DECIMAL_PLACES) || 0,
        Decimal.ROUND_DOWN,
    );

    return `${numerator}/1`;
};

/**
 * Formats odds based on the specified display format.
 * @param  format - The odds display format (EU, UK, or US).
 * @param  odds - The odds object containing decimal, fractional, or moneyline values.
 * @param  input - Input parameters for formatting options.
 * @returns  - Formatted odds string based on the specified format.
 */
export const formatOddsInternal = (format: OddsDisplayFormat, odds: PartialOdds, input: Required<OddsFormatInput>) => {
    switch (format) {
        case OddsDisplayFormat.EU:
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return formatDecimals(odds.decimals!, input.cutZero, input.isSummary, input.maxDecimalPlaces);
        case OddsDisplayFormat.UK:
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return formatFractional(odds.fractional!, input.isSummary, input.maxDecimalPlaces, input.useOddsMultiplicationFormat);
        case OddsDisplayFormat.US:
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return formatMoneyLine(odds.moneyline!, input.roundPriceValues);
    }
};

/**
 * Formats moneyline odds as a string, with an option for rounding values.
 * @param  moneyline - The moneyline odds value.
 * @param  roundPriceValues - If true, round moneyline odds according to configuration.
 * @returns  - Formatted moneyline odds string.
 */
export const formatMoneyLine = (moneyline: number | Decimal, roundPriceValues: boolean) => {
    if (typeof moneyline === 'number') {
        moneyline = new Decimal(moneyline);
    }

    const moneyLineString = roundAmericanOdds(moneyline, roundPriceValues);

    return moneyline.greaterThan(0) ? `+${moneyLineString}` : `${moneyLineString}`;
};

/**
 * Formats decimal odds to a string, applying specified formatting rules.
 * @param  decimals - The decimal odds value.
 * @param  cutZero - If true, removes trailing zeros from the decimal value.
 * @param  isSummary - Indicates if the summary format should be used.
 * @param  maxDecimalPlaces - Maximum number of decimal places to display.
 * @returns  - Formatted decimal odds string.
 */
export const formatDecimals = (decimals: number | Decimal, cutZero: boolean, isSummary: boolean, maxDecimalPlaces: number) => {
    if (typeof decimals === 'number') {
        if (decimals > 1 && decimals < 1.01) {
            return decimals.toFixed(3);
        }
        decimals = new Decimal(decimals);
    }

    if (cutZero && decimals.decimalPlaces() > DECIMAL_PLACES) {
        const result = decimals.toFixed(decimals.decimalPlaces() > DECIMAL_PLACES ? maxDecimalPlaces : DECIMAL_PLACES, Decimal.ROUND_DOWN);

        return result.endsWith('0') ? decimals.toFixed(DECIMAL_PLACES, Decimal.ROUND_DOWN) : result;
    }

    return decimals.toFixed(
        decimals.decimalPlaces() > DECIMAL_PLACES && (!isSummary || decimals.lessThan(SUMMARY_VALUE_THRESHOLD)) ? maxDecimalPlaces : DECIMAL_PLACES,
        Decimal.ROUND_DOWN,
    );
};

/**
 * Formats fractional odds to a string based on summary or multiplication format options.
 * @param - The fractional odds data.
 * @param  isSummary - Indicates if the summary format should be used.
 * @param maxDecimalPlaces - Maximum number of decimal places to use in formatting.
 * @param  useOddsMultiplicationFormat - Indicates if multiplication format should be used.
 * @returns  - Formatted fractional odds string.
 */
export const formatFractional = (
    fractional: { numerator: number; denominator: number } | Fraction,
    isSummary: boolean,
    maxDecimalPlaces: number,
    useOddsMultiplicationFormat: boolean,
) => {
    if (isSummary || useOddsMultiplicationFormat) {
        return toSimplifiedUKOdds(fractional, maxDecimalPlaces, isSummary);
    }
    const decimalPlaces = Math.min(new Decimal(fractional.numerator).decimalPlaces(), maxDecimalPlaces) || 0;

    return `${fractional.numerator.toFixed(decimalPlaces)}/${fractional.denominator}`;
};

/**
 * Attempts to format odds in the main format; if not valid, falls back to alternative formats.
 * @param  mainFormat - The primary format to attempt.
 * @param fallbackFormats - List of fallback formats to use if the main format is invalid.
 * @param  odds - The odds object containing potential decimal, fractional, or moneyline values.
 * @param  input - Formatting options for the odds.
 * @returns  - Formatted odds string based on main or fallback format.
 */
export const formatOddsWithFallback = (
    mainFormat: OddsDisplayFormat,
    fallbackFormats: OddsDisplayFormat[],
    odds: PartialOdds,
    input: Required<OddsFormatInput>,
) => {
    if (isFormatValid(mainFormat, odds)) {
        return formatOddsInternal(mainFormat, odds, input);
    }
    for (const format of fallbackFormats) {
        if (isFormatValid(format, odds)) {
            const newOdds = OddsConverter.convertOdds(mainFormat, format, odds);

            return formatOddsInternal(mainFormat, newOdds, input);
        }
    }

    return formatInvalidOdds(mainFormat);
};

/**
 * Formats user odds by attempting to match the display format, with fallbacks for invalid formats.
 * @param odds - The odds object to format, which may contain decimals, fractional, or moneyline values.
 * @param  input - Formatting options including display format and fallback settings.
 * @returns  - Formatted odds as a string.
 */
export const toFormattedOdds = (odds: PartialOdds, input: Required<OddsFormatInput>) => {
    switch (input.displayFormat) {
        case OddsDisplayFormat.EU: {
            return formatOddsWithFallback(OddsDisplayFormat.EU, [OddsDisplayFormat.UK, OddsDisplayFormat.US], odds, input);
        }
        case OddsDisplayFormat.UK: {
            return formatOddsWithFallback(OddsDisplayFormat.UK, [OddsDisplayFormat.EU, OddsDisplayFormat.US], odds, input);
        }
        case OddsDisplayFormat.US: {
            return formatOddsWithFallback(OddsDisplayFormat.US, [OddsDisplayFormat.UK, OddsDisplayFormat.EU], odds, input);
        }
    }
};

/**
 * Formats odds using configuration input, converting formats if needed and handling invalid odds.
 * @param  odds - The odds object, which may contain decimals, fractional, moneyline, or all.
 * @param  _input - Input parameters for odds display formatting.
 * @returns  - The fully formatted odds string based on provided configuration.
 */
export const formatOdds = (odds: CalculatedOdds | Odds, _input: OddsFormatInput) => {
    const input = { ...defaultOddsFormatInput, ..._input } as Required<OddsFormatInput>;

    if (!odds || !OddsOperations.isOddsValid(odds)) {
        return formatInvalidOdds(input.displayFormat);
    }

    if (input.fromFormat && input.displayFormat !== input.fromFormat) {
        const convertedOdds = OddsConverter.convertOdds(input.displayFormat, input.fromFormat, odds);

        return toFormattedOdds(convertedOdds, input);
    }

    return toFormattedOdds(odds, input);
};

@Injectable({ providedIn: 'root' })
export class OddsFormatterService {
    betslipConfig = inject(BetslipConfig);

    /**
     * Formats odds according to user input, including optional conversion to another format.
     * @param  odds - The odds object to format.
     * @param  input - Input parameters to customize the odds formatting process.
     * @returns  - The formatted odds as a string, or an invalid representation if odds are not valid.
     */
    format(odds: Odds | CalculatedOdds | null | undefined, input: OddsFormatInput): string {
        if (!odds || !OddsOperations.isOddsValid(odds)) {
            return formatInvalidOdds(input.displayFormat);
        }

        return formatOdds(odds, {
            ...input,
            roundPriceValues: this.betslipConfig.roundPriceValues,
        });
    }
}
